import { API_BASE_URL, timeout } from "@/consts/sys_config";
import { getCookie, getPixelId } from "@/lib/facebook-pixel";
import { monitor } from "@/lib/sentry/instrument";
import { getToken } from "@/store/auth";
import { async_try } from "@/utils/error";
import { getDeviceCode, getNativeAppVersion, getNativeChannel, inApp } from "@/utils/interface";
import { is_auth } from "@/utils/is_auth";
import * as Sentry from "@sentry/nextjs";
import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";

/**
 * 统一请求层。
 *
 * 这个文件除了做 token、渠道、设备号等请求封装，
 * 现在还承担了“HTTP 错误分类上报”的职责：
 * - request_setup: 请求发出前配置异常
 * - network: 超时、断网、网络失败
 * - http: 真正的 HTTP 层失败
 * - biz: 仅对白名单业务码上报
 *
 * 设计目标：
 * 1. 不把所有业务失败都灌进 Sentry
 * 2. 只保留对排查有价值的错误
 * 3. 保持请求层是唯一的 HTTP 错误上报入口，避免业务代码到处零散 capture
 */

const SENTRY_BIZ_ERROR_CODES = new Set<number | string>(
    ( process.env.NEXT_PUBLIC_SENTRY_BIZ_ERROR_CODES || "" ).split( "," ).map( value => value.trim() ).filter( Boolean )
        .flatMap( value => {
            const numericValue = Number( value );
            return Number.isNaN( numericValue ) ? [ value ] : [ value, numericValue ];
        } ),
);
const packageId = process.env.NEXT_PUBLIC_PACKAGE_ID;

function isReportableBizErrorCode( code: unknown ) {
    return code !== undefined && SENTRY_BIZ_ERROR_CODES.has( code as number | string );
}

function getApiBaseURL() {
    const domain = process.env.NEXT_PUBLIC_DOMAIN || "";
    return `${domain.replace( /\/$/, "" )}${API_BASE_URL}`;
}

const createAxiosInstance = ( baseURL: string ) => {
    const instance = axios.create( {
        baseURL,
        timeout, // 请求的超时时间
        transformRequest: [ function( data, headers ) {
            // 对发送的 data 进行任意转换处理
            // 如果 Content-Type 是 application/json，需要序列化
            if ( headers["Content-Type"] === "application/json" && typeof data === "object" ) {
                return JSON.stringify( data );
            }
            return data;
        } ],
    } );

    // 添加请求拦截器
    instance.interceptors.request.use( function( config: InternalAxiosRequestConfig ) {
        const token = getToken();
        if ( token ) {
            config.headers["token"] = token;
        }
        const isApp = inApp();
        const nativeVersionCode = getNativeAppVersion();
        const nativeChannel = getNativeChannel();
        const fallbackVersionCode = isApp ? ( nativeVersionCode || "1.0.1" ) : "999";
        const rawVersionCode = config.data?.versionCode ?? fallbackVersionCode;
        const normalizedVersionCode = Number( String( rawVersionCode ).replace( /\./g, "" ) );
        config.data = {
            channel: isApp ? "app" : "h5",
            channelId: window.channel || nativeChannel || "yes_no",
            deviceCode: getDeviceCode(),
            ...config.data,
            versionCode: Number.isFinite( normalizedVersionCode ) ? normalizedVersionCode : 999,
        };

        config.headers["packageId"] = packageId;
        config.headers["x-origin"] = isApp ? "app" : "h5";

        const pixelId = getPixelId();
        if ( pixelId ) config.headers["pixelid"] = pixelId;
        const fbp = getCookie( "_fbp" );
        if ( fbp ) config.headers["fbp"] = fbp;
        const fbc = getCookie( "_fbc" );
        if ( fbc ) config.headers["fbc"] = fbc;
        if ( config.method === "post" && config.data ) {
            if ( config.data?.cancelToken ) {
                config.cancelToken = config.data.cancelToken;
                delete config.data.cancelToken;
            }
            config.headers["Content-Type"] = "application/json";
            // 保持 data 为普通对象，axios 会自动序列化成 JSON
        }

        return config;
    }, function( error ) {
        monitor.network( {
            kind: "request_setup",
            method: error?.config?.method,
            url: error?.config?.url,
            message: error?.message,
        }, error );
        return Promise.reject( error );
    } );

    // 添加响应拦截器
    instance.interceptors.response.use( function( response ) {
        // 去除登录验证请求
        // if ( response?.data?.code === -1 ) {
        //     const hasToken = !!getToken();
        //     if ( hasToken ) {
        //         notifyLoginExpired();
        //     } else {
        //         openLoginModal( "manual" );
        //     }
        // }
        if ( response?.config?.responseType === "blob" ) return async_try( response.data ) as any;
        if ( Number( response.status ) === 200 && response.data && Number( response.data.code ) === 0 ) {
            const response_data: any = response.data;
            response_data._origin = response;
            return async_try( response_data );
        } else {
            const reject_data: any = response.data;
            if ( reject_data?.code === 1234 ) {
                reject_data.msg = "";
                reject_data.silentToast = true;
                reject_data._origin = response;
                return async_try( reject_data );
            }

            if ( reject_data?.code !== -1 && isReportableBizErrorCode( reject_data?.code ) ) {
                // 只对白名单业务码上报，避免把大量用户可恢复错误灌进 Sentry。
                const activeSpan = Sentry.getActiveSpan();
                const traceId = activeSpan?.spanContext?.()?.traceId;

                monitor.network( {
                    kind: "biz",
                    method: response.config?.method,
                    url: response.config?.url,
                    status: response.status,
                    code: reject_data?.code,
                    traceId,
                    message: reject_data?.msg,
                    responseData: reject_data,
                } );
            }

            reject_data._origin = response;
            return async_try( Promise.reject( reject_data ) );
        }
    }, function( error: AxiosError<any> ) {
        /**
         * AbortController
         *
         * 必须过滤
         */
        if ( error.code === AxiosError.ERR_CANCELED || error.message?.includes( "canceled" ) ) {
            return async_try( Promise.reject( error ) );
        }

        const isNetworkError = error.code === AxiosError.ERR_NETWORK
            || error.code === AxiosError.ETIMEDOUT
            || error.code === "ECONNABORTED"
            || error.message?.includes( "Network Error" )
            || error.message?.includes( "timeout" );

        if ( isNetworkError ) {
            error.message = "Network exception, please try again later";
        }

        const activeSpan = Sentry.getActiveSpan();
        const traceId = activeSpan?.spanContext?.()?.traceId;

        monitor.network( {
            kind: isNetworkError ? "network" : "http",
            method: error.config?.method,
            url: error.config?.url,
            status: error.response?.status,
            code: error.code,
            traceId,
            message: error.message,
            responseData: error.response?.data,
        }, error );

        return async_try( Promise.reject( error ) );
    } );

    const axios_get = instance.get.bind( instance );
    const axios_post = instance.post.bind( instance );

    function create_cancelable_request( request_fn: any, url: string, ...args: any[] ) {
        url = url.startsWith( "/" ) ? url.trim() : `/${url}`.trim();

        if ( !is_auth( url ) ) {
            return args.at( -1 )
                ? [ Promise.resolve( [ false, "Unauthorized", "async_try" ] ), { cancel: () => "" } ]
                : Promise.resolve( [ false, "Unauthorized", "async_try" ] );
        }

        if ( !args.at( -1 ) ) {
            return request_fn( url, ...args.slice( 0, -1 ) ) as any;
        }

        const controller = new AbortController();

        const cancelable_ret = [
            request_fn( url, ...args.slice( 0, -2 ), { ...args.at( -2 ), signal: controller.signal } ),
            { cancel: ( ...args: any[] ) => controller.abort( ...args ) },
        ];

        return cancelable_ret;
    }

    instance.get = function( url: string, config: AxiosRequestConfig<any>, can_cancel: boolean = false ) {
        return create_cancelable_request( axios_get, url, config, can_cancel );
    } as any;

    instance.post = function(
        url: string,
        params: any,
        config: AxiosRequestConfig<any> = {},
        can_cancel: boolean = false,
    ) {
        return create_cancelable_request( axios_post, url, params, config, can_cancel );
    } as any;

    return instance;
};

const defaultInstance = createAxiosInstance( getApiBaseURL() );

export { createAxiosInstance, defaultInstance as axios };
